import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { getAuth } from 'firebase/auth';
import { 
  getStartupOpportunities, 
  deleteStartupOpportunity,
  getStartupOpportunityById,
  updateStartupOpportunity,
  updateOpportunityStatus
} from '../../services/firebase/startup-opportunities';
import { getOpportunityAnalytics } from '../../services/firebase/opportunity-analytics';
import OpportunityFormModal from '../features/dashboard/jobs/OpportunityFormModal';
import OpportunityCard from '../features/dashboard/jobs/OpportunityCard';
import OpportunityDetailsModal from '../features/dashboard/jobs/OpportunityDetailsModal';
import { StartupOpportunityUtils } from '../../utils/startupOpportunityModels';

export default function OpportunityManagementView() {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [opportunityToDelete, setOpportunityToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOpportunities, setSelectedOpportunities] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [fundingStageFilter, setFundingStageFilter] = useState('all');
  const [roleAreaFilter, setRoleAreaFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);

  // Fetch opportunities
  useEffect(() => {
    fetchOpportunities();
  }, [statusFilter, fundingStageFilter, roleAreaFilter, sortBy, sortDirection]);

  const fetchOpportunities = async () => {
    setLoading(true);
    try {
      const queryOptions = {
        limit: 50,
        orderByField: sortBy,
        orderDirection: sortDirection
      };
      
      // Add status filter if not 'all'
      if (statusFilter !== 'all') {
        queryOptions.status = statusFilter;
      }
      
      // Add funding stage filter if not 'all'
      if (fundingStageFilter !== 'all') {
        queryOptions.companyStages = [fundingStageFilter];
      }
      
      // Add role area filter if not 'all'
      if (roleAreaFilter !== 'all') {
        queryOptions.roleAreas = [roleAreaFilter];
      }
      
      const result = await getStartupOpportunities(queryOptions);

      if (result.success) {
        setOpportunities(result.data.opportunities);
      } else {
        toast.error(`Failed to load opportunities: ${result.error}`);
      }
    } catch (error) {
      console.error("Error fetching opportunities:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle opening form for new opportunity
  const handleAddOpportunity = () => {
    setSelectedOpportunity(null);
    setIsFormOpen(true);
  };

  // Handle opening form for editing opportunity
  const handleEditOpportunity = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setIsFormOpen(true);
  };
  
  // Handle duplicating an opportunity
  const handleDuplicateOpportunity = (opportunity) => {
    // Create a copy of the opportunity without the ID and timestamps
    const duplicatedOpportunity = {
      ...opportunity,
      companyName: `${opportunity.companyName} (Copy)`,
      status: 'paused', // Start as paused to prevent accidental publishing
      createdAt: null,
      updatedAt: null
    };
    
    // Remove the ID so a new one will be generated
    delete duplicatedOpportunity.id;
    
    // Open the form with the duplicated data
    setSelectedOpportunity(duplicatedOpportunity);
    setIsFormOpen(true);
    
    // Show notification
    toast.success('Created a copy of the opportunity. Edit details and save to create a new listing.', {
      duration: 4000
    });
  };

  // Handle viewing opportunity details
  const handleViewDetails = async (opportunityId) => {
    setIsFormLoading(true);
    try {
      const result = await getStartupOpportunityById(opportunityId);
      if (result.success) {
        setSelectedOpportunity(result.data);
        setIsDetailsOpen(true);
      } else {
        toast.error(`Failed to load opportunity details: ${result.error}`);
      }
    } catch (error) {
      console.error("Error fetching opportunity details:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsFormLoading(false);
    }
  };

  // Handle preview opportunity (shows how it appears to candidates)
  const handlePreviewOpportunity = async (opportunity) => {
    setIsFormLoading(true);
    try {
      // Fetch the full opportunity data if we only have summary
      let fullOpportunity = opportunity;
      if (!opportunity.founderInfo || !opportunity.companyMetrics) {
        const result = await getStartupOpportunityById(opportunity.id);
        if (result.success) {
          fullOpportunity = result.data;
        }
      }
      
      // Show toast notification to indicate this is a preview
      toast.success('Previewing opportunity as seen by candidates', {
        icon: '👁️',
        duration: 3000
      });
      
      setSelectedOpportunity(fullOpportunity);
      setIsDetailsOpen(true);
    } catch (error) {
      console.error("Error fetching opportunity for preview:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsFormLoading(false);
    }
  };
  
  // Fetch analytics data for an opportunity
  const fetchOpportunityAnalytics = async (opportunityId) => {
    try {
      const result = await getOpportunityAnalytics(opportunityId);
      
      if (result.success) {
        return result.data.metrics;
      } else {
        console.error("Error fetching analytics:", result.error);
        return null;
      }
    } catch (error) {
      console.error("Error fetching opportunity analytics:", error);
      return null;
    }
  };
  
  // Handle viewing analytics for an opportunity
  const handleViewAnalytics = async (opportunity) => {
    setSelectedOpportunity(opportunity);
    setIsLoadingAnalytics(true);
    setShowAnalyticsModal(true);
    
    try {
      const analytics = await fetchOpportunityAnalytics(opportunity.id);
      setAnalyticsData(analytics || {});
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load analytics data");
      setAnalyticsData({});
    } finally {
      setIsLoadingAnalytics(false);
    }
  };

  // Handle closing form
  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedOpportunity(null);
  };

  // Handle closing details modal
  const handleCloseDetails = () => {
    setIsDetailsOpen(false);
    setSelectedOpportunity(null);
  };

  // Handle successful form submission
  const handleFormSuccess = (savedOpportunity) => {
    if (selectedOpportunity) {
      // Update existing opportunity
      setOpportunities(prev => 
        prev.map(opp => opp.id === savedOpportunity.id ? savedOpportunity : opp)
      );
      toast.success('Opportunity updated successfully');
    } else {
      // Add new opportunity
      setOpportunities(prev => [savedOpportunity, ...prev]);
      toast.success('Opportunity created successfully');
    }
  };

  // Handle delete initiation
  const handleDeleteClick = (opportunity) => {
    setOpportunityToDelete(opportunity);
    setShowDeleteConfirm(true);
  };

  // Handle delete confirmation
  const confirmDelete = async () => {
    if (!opportunityToDelete) return;

    setIsDeleting(true);
    try {
      const result = await deleteStartupOpportunity(opportunityToDelete.id);
      
      if (result.success) {
        toast.success('Opportunity deleted successfully');
        setOpportunities(prev => prev.filter(opp => opp.id !== opportunityToDelete.id));
      } else {
        toast.error(`Failed to delete opportunity: ${result.error}`);
      }
    } catch (error) {
      console.error("Error deleting opportunity:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setOpportunityToDelete(null);
    }
  };

  // Handle status change
  const handleStatusChange = async (opportunity, newStatus) => {
    try {
      // Show loading state
      const loadingToast = toast.loading(`Updating status to ${newStatus}...`);
      
      // Update in Firebase
      const result = await updateStartupOpportunity(opportunity.id, {
        status: newStatus,
        updatedAt: new Date()
      });
      
      toast.dismiss(loadingToast);
      
      if (result.success) {
        // Update local state
        const updatedOpportunity = { ...opportunity, status: newStatus };
        setOpportunities(prev => 
          prev.map(opp => opp.id === opportunity.id ? updatedOpportunity : opp)
        );
        
        toast.success(`Opportunity status changed to ${newStatus}`);
      } else {
        toast.error(`Failed to update status: ${result.error}`);
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(`Error: ${error.message}`);
    }
  };

  // Handle bulk status change
  const handleBulkStatusChange = async (newStatus) => {
    if (selectedOpportunities.length === 0) return;
    
    try {
      const loadingToast = toast.loading(`Updating ${selectedOpportunities.length} opportunities to ${newStatus}...`);
      
      // Update all selected opportunities
      const updatePromises = selectedOpportunities.map(opportunityId => 
        updateStartupOpportunity(opportunityId, {
          status: newStatus,
          updatedAt: new Date()
        })
      );
      
      const results = await Promise.all(updatePromises);
      toast.dismiss(loadingToast);
      
      // Check if all updates were successful
      const failedUpdates = results.filter(result => !result.success);
      
      if (failedUpdates.length === 0) {
        // Update local state for all selected opportunities
        setOpportunities(prev => 
          prev.map(opp => 
            selectedOpportunities.includes(opp.id) 
              ? { ...opp, status: newStatus }
              : opp
          )
        );
        
        toast.success(`${selectedOpportunities.length} opportunities updated to ${newStatus}`);
        setSelectedOpportunities([]);
        setShowBulkActions(false);
      } else {
        toast.error(`${failedUpdates.length} updates failed. Please try again.`);
      }
    } catch (error) {
      console.error("Error updating bulk status:", error);
      toast.error(`Error: ${error.message}`);
    }
  };

  // Handle opportunity selection
  const handleOpportunitySelect = (opportunityId, isSelected) => {
    if (isSelected) {
      setSelectedOpportunities(prev => [...prev, opportunityId]);
    } else {
      setSelectedOpportunities(prev => prev.filter(id => id !== opportunityId));
    }
  };

  // Handle select all
  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedOpportunities(filteredOpportunities.map(opp => opp.id));
    } else {
      setSelectedOpportunities([]);
    }
  };

  // Filter opportunities based on search term
  const filteredOpportunities = opportunities.filter(opportunity => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      opportunity.companyName?.toLowerCase().includes(searchLower) ||
      opportunity.roleArea?.toLowerCase().includes(searchLower) ||
      opportunity.fundingStage?.toLowerCase().includes(searchLower) ||
      opportunity.companyDescription?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Startup Opportunities</h2>
          <p className="text-gray-600">Manage startup opportunity listings and status</p>
        </div>
        <button
          onClick={handleAddOpportunity}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Opportunity
        </button>
      </div>

      {/* Statistics Cards */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-semibold text-sm">📊</span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total</p>
                <p className="text-lg font-semibold text-gray-900">{opportunities.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 font-semibold text-sm">🟢</span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Active</p>
                <p className="text-lg font-semibold text-gray-900">
                  {opportunities.filter(opp => opp.status === 'active').length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                  <span className="text-yellow-600 font-semibold text-sm">🟡</span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Paused</p>
                <p className="text-lg font-semibold text-gray-900">
                  {opportunities.filter(opp => opp.status === 'paused').length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <span className="text-gray-600 font-semibold text-sm">⚫</span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Filled</p>
                <p className="text-lg font-semibold text-gray-900">
                  {opportunities.filter(opp => opp.status === 'filled').length}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex flex-col gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by company name, role area, or funding stage..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="filled">Filled</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Funding Stage</label>
              <select
                value={fundingStageFilter}
                onChange={(e) => setFundingStageFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Stages</option>
                {StartupOpportunityUtils.getFundingStages().map(stage => (
                  <option key={stage} value={stage}>
                    {StartupOpportunityUtils.formatFundingStage(stage)}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Role Area</label>
              <select
                value={roleAreaFilter}
                onChange={(e) => setRoleAreaFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Role Areas</option>
                {StartupOpportunityUtils.getRoleAreas().map(area => (
                  <option key={area} value={area}>
                    {StartupOpportunityUtils.formatRoleArea(area)}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
              <div className="flex gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="createdAt">Created Date</option>
                  <option value="updatedAt">Updated Date</option>
                  <option value="companyName">Company Name</option>
                </select>
                <button
                  onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                  className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}
                >
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedOpportunities.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center">
              <span className="text-sm font-medium text-blue-900">
                {selectedOpportunities.length} opportunity{selectedOpportunities.length !== 1 ? 'ies' : ''} selected
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-blue-700">Bulk actions:</span>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleBulkStatusChange('active')}
                  className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200 flex items-center"
                >
                  <span className="mr-1">🟢</span> Set Active
                </button>
                <button
                  onClick={() => handleBulkStatusChange('paused')}
                  className="px-3 py-1 text-xs font-medium text-yellow-700 bg-yellow-100 rounded hover:bg-yellow-200 flex items-center"
                >
                  <span className="mr-1">🟡</span> Set Paused
                </button>
                <button
                  onClick={() => handleBulkStatusChange('filled')}
                  className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 flex items-center"
                >
                  <span className="mr-1">⚫</span> Set Filled
                </button>
                <button
                  onClick={() => {
                    setSelectedOpportunities([]);
                    setShowBulkActions(false);
                  }}
                  className="px-3 py-1 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded hover:bg-gray-50"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
          
          <div className="mt-3 text-xs text-blue-700">
            <p>
              <strong>Tip:</strong> Use bulk actions to quickly update multiple opportunities at once. 
              For example, you can pause all opportunities for a specific company or mark all filled positions.
            </p>
          </div>
        </div>
      )}

      {/* Opportunities Grid */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading opportunities...</span>
        </div>
      ) : filteredOpportunities.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6.5" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No opportunities found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || statusFilter !== 'all' 
              ? 'Try adjusting your search or filters.' 
              : 'Get started by creating a new opportunity.'
            }
          </p>
          {!searchTerm && statusFilter === 'all' && (
            <div className="mt-6">
              <button
                onClick={handleAddOpportunity}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Opportunity
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Select All Header */}
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={selectedOpportunities.length === filteredOpportunities.length && filteredOpportunities.length > 0}
                onChange={(e) => handleSelectAll(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 text-sm font-medium text-gray-700">
                Select all ({filteredOpportunities.length} opportunities)
              </label>
            </div>
          </div>

          {filteredOpportunities.map((opportunity) => (
            <div key={opportunity.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex justify-between items-start">
                <div className="flex items-start gap-3 flex-1">
                  <input
                    type="checkbox"
                    checked={selectedOpportunities.includes(opportunity.id)}
                    onChange={(e) => handleOpportunitySelect(opportunity.id, e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{opportunity.companyName}</h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        opportunity.status === 'active' ? 'bg-green-100 text-green-800' :
                        opportunity.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {opportunity.status}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 mb-3">
                      <div>
                        <span className="font-medium">Role Area:</span> {opportunity.roleArea}
                      </div>
                      <div>
                        <span className="font-medium">Funding Stage:</span> {opportunity.fundingStage}
                      </div>
                      <div>
                        <span className="font-medium">Experience:</span> {opportunity.experienceLevel}
                      </div>
                    </div>
                    
                    <p className="text-gray-700 text-sm line-clamp-2">{opportunity.companyDescription}</p>
                    
                    {opportunity.leadInvestors && opportunity.leadInvestors.length > 0 && (
                      <div className="mt-2">
                        <span className="text-xs text-gray-500">Investors: </span>
                        <span className="text-xs text-gray-700">{opportunity.leadInvestors.join(', ')}</span>
                      </div>
                    )}
                    
                    <div className="mt-3 text-xs text-gray-500">
                      Created: {opportunity.createdAt ? new Date(opportunity.createdAt.seconds * 1000).toLocaleDateString() : 'Unknown'}
                      {opportunity.updatedAt && (
                        <span className="ml-4">
                          Updated: {new Date(opportunity.updatedAt.seconds * 1000).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col gap-2 ml-4">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePreviewOpportunity(opportunity)}
                      className="flex-1 px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
                      title="Preview how this opportunity appears to candidates"
                      disabled={isFormLoading}
                    >
                      {isFormLoading ? '⏳' : '👁️'} Preview
                    </button>
                    <button
                      onClick={() => handleEditOpportunity(opportunity)}
                      className="flex-1 px-3 py-1 text-xs font-medium text-gray-600 bg-gray-50 rounded hover:bg-gray-100 transition-colors"
                    >
                      ✏️ Edit
                    </button>
                  </div>
                  
                  <button
                    onClick={() => handleDuplicateOpportunity(opportunity)}
                    className="px-3 py-1 text-xs font-medium text-purple-600 bg-purple-50 rounded hover:bg-purple-100 transition-colors"
                    title="Create a copy of this opportunity"
                  >
                    📋 Duplicate
                  </button>
                  
                  <div className="relative">
                    <label className="block text-xs text-gray-500 mb-1">Status:</label>
                    <select
                      value={opportunity.status}
                      onChange={(e) => handleStatusChange(opportunity, e.target.value)}
                      className={`w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors ${
                        opportunity.status === 'active' ? 'border-green-300 bg-green-50 text-green-800' :
                        opportunity.status === 'paused' ? 'border-yellow-300 bg-yellow-50 text-yellow-800' :
                        'border-gray-300 bg-gray-50 text-gray-800'
                      }`}
                      title="Change opportunity status"
                    >
                      <option value="active">🟢 Active - Visible to candidates</option>
                      <option value="paused">🟡 Paused - Temporarily hidden</option>
                      <option value="filled">⚫ Filled - Position closed</option>
                    </select>
                  </div>
                  
                  <div className="mt-2 text-xs text-gray-500">
                    <div className="flex justify-between">
                      <span>Views: {opportunity.viewCount || 0}</span>
                      <span>Connections: {opportunity.connectionRequestCount || 0}</span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewAnalytics(opportunity);
                      }}
                      className="mt-1 w-full px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 rounded hover:bg-indigo-100 transition-colors"
                    >
                      📊 View Analytics
                    </button>
                  </div>
                  
                  <button
                    onClick={() => handleDeleteClick(opportunity)}
                    className="mt-2 px-3 py-1 text-xs font-medium text-red-600 bg-red-50 rounded hover:bg-red-100 transition-colors"
                    title="Delete this opportunity permanently"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Opportunity Form Modal */}
      <OpportunityFormModal
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        opportunity={selectedOpportunity}
        onSuccess={handleFormSuccess}
      />

      {/* Opportunity Details Modal */}
      <OpportunityDetailsModal
        isOpen={isDetailsOpen}
        onClose={handleCloseDetails}
        opportunity={selectedOpportunity}
        showConnectionButton={false}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden w-full max-w-md p-6">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Delete Opportunity</h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    Are you sure you want to delete "{opportunityToDelete?.companyName}"? This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
              <button
                type="button"
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setOpportunityToDelete(null);
                }}
                disabled={isDeleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Analytics Modal */}
      {showAnalyticsModal && selectedOpportunity && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden w-full max-w-4xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Analytics for {selectedOpportunity.companyName}
              </h2>
              <button
                onClick={() => {
                  setShowAnalyticsModal(false);
                  setSelectedOpportunity(null);
                  setAnalyticsData(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {isLoadingAnalytics ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading analytics data...</span>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Overview Metrics */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Overview</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
                      <div className="text-sm font-medium text-gray-500">Total Views</div>
                      <div className="text-2xl font-bold text-gray-900">{analyticsData?.viewCount || 0}</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
                      <div className="text-sm font-medium text-gray-500">Detail Views</div>
                      <div className="text-2xl font-bold text-gray-900">{analyticsData?.detailsViewCount || 0}</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
                      <div className="text-sm font-medium text-gray-500">Connection Requests</div>
                      <div className="text-2xl font-bold text-gray-900">{analyticsData?.connectionRequestCount || 0}</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
                      <div className="text-sm font-medium text-gray-500">Successful Connections</div>
                      <div className="text-2xl font-bold text-gray-900">{analyticsData?.connectionIntroducedCount || 0}</div>
                    </div>
                  </div>
                </div>
                
                {/* Conversion Metrics */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Conversion Rates</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
                      <div className="text-sm font-medium text-gray-500">View to Detail Rate</div>
                      <div className="text-2xl font-bold text-gray-900">
                        {analyticsData?.viewCount ? 
                          `${((analyticsData.detailsViewCount || 0) / analyticsData.viewCount * 100).toFixed(1)}%` : 
                          '0%'}
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
                      <div className="text-sm font-medium text-gray-500">Detail to Connection Rate</div>
                      <div className="text-2xl font-bold text-gray-900">
                        {analyticsData?.detailsViewCount ? 
                          `${((analyticsData.connectionRequestCount || 0) / analyticsData.detailsViewCount * 100).toFixed(1)}%` : 
                          '0%'}
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
                      <div className="text-sm font-medium text-gray-500">Connection Success Rate</div>
                      <div className="text-2xl font-bold text-gray-900">
                        {analyticsData?.connectionRequestCount ? 
                          `${((analyticsData.connectionIntroducedCount || 0) / analyticsData.connectionRequestCount * 100).toFixed(1)}%` : 
                          '0%'}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Activity Timeline */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
                  <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
                    <div className="space-y-4">
                      {analyticsData?.lastViewedAt && (
                        <div className="flex items-start">
                          <div className="flex-shrink-0 h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-blue-600 text-xs">👁️</span>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm text-gray-700">Last viewed</p>
                            <p className="text-xs text-gray-500">
                              {new Date(analyticsData.lastViewedAt.seconds * 1000).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {analyticsData?.lastConnectionRequestAt && (
                        <div className="flex items-start">
                          <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center">
                            <span className="text-green-600 text-xs">🔗</span>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm text-gray-700">Last connection request</p>
                            <p className="text-xs text-gray-500">
                              {new Date(analyticsData.lastConnectionRequestAt.seconds * 1000).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {analyticsData?.lastSuccessfulConnectionAt && (
                        <div className="flex items-start">
                          <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center">
                            <span className="text-green-600 text-xs">✅</span>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm text-gray-700">Last successful connection</p>
                            <p className="text-xs text-gray-500">
                              {new Date(analyticsData.lastSuccessfulConnectionAt.seconds * 1000).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {!analyticsData?.lastViewedAt && !analyticsData?.lastConnectionRequestAt && !analyticsData?.lastSuccessfulConnectionAt && (
                        <p className="text-sm text-gray-500">No recent activity recorded</p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Connection Status */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Connection Status</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
                      <div className="text-sm font-medium text-gray-500">Approved</div>
                      <div className="text-2xl font-bold text-blue-600">{analyticsData?.connectionApprovedCount || 0}</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
                      <div className="text-sm font-medium text-gray-500">Introduced</div>
                      <div className="text-2xl font-bold text-green-600">{analyticsData?.connectionIntroducedCount || 0}</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
                      <div className="text-sm font-medium text-gray-500">Declined</div>
                      <div className="text-2xl font-bold text-red-600">{analyticsData?.connectionDeclinedCount || 0}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}